/**
 * Format file size in bytes to human-readable string.
 * Uses 1024-based units: B, KB, MB, GB, TB.
 */
export function formatSize(bytes: number, fractionDigits = 2): string {
  if (bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  const k = 1024
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), units.length - 1)
  const value = parseFloat((bytes / Math.pow(k, i)).toFixed(fractionDigits))
  return `${value} ${units[i]}`
}

/**
 * Format a timestamp or Date to "YYYY-MM-DD HH:mm:ss" with zero-padding.
 */
export function formatTime(input: number | Date): string {
  const date = input instanceof Date ? input : new Date(input)
  if (isNaN(date.getTime())) return 'Invalid Date'
  const pad = (n: number) => String(n).padStart(2, '0')
  return [
    [date.getFullYear(), pad(date.getMonth() + 1), pad(date.getDate())].join('-'),
    [pad(date.getHours()), pad(date.getMinutes()), pad(date.getSeconds())].join(':'),
  ].join(' ')
}

/**
 * Get file extension from filename or path.
 * Returns extension with leading dot, e.g. ".jpg", ".tar.gz".
 * Returns empty string if no extension.
 */
export function getExtension(filename: string): string {
  const name = filename.replace(/^.*[/\\]/, '')
  const dotIndex = name.indexOf('.')
  if (dotIndex === -1) return ''
  return name.slice(dotIndex).toLowerCase()
}

/**
 * Get file stem (filename without extension) from filename or path.
 */
export function getStem(filename: string): string {
  const name = filename.replace(/^.*[/\\]/, '')
  const dotIndex = name.indexOf('.')
  return dotIndex === -1 ? name : name.slice(0, dotIndex)
}

/**
 * Extract base filename from a full path (cross-platform).
 */
export function getBaseName(filepath: string): string {
  return filepath.replace(/^.*[/\\]/, '')
}

/**
 * Infer MIME type from file extension.
 * Covers the most common types.
 */
export function inferMimeType(filename: string): string {
  const ext = getExtension(filename)
  const map: Record<string, string> = {
    // Images
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.gif': 'image/gif',
    '.webp': 'image/webp',
    '.svg': 'image/svg+xml',
    '.bmp': 'image/bmp',
    '.ico': 'image/x-icon',
    '.avif': 'image/avif',
    '.tiff': 'image/tiff',
    '.tif': 'image/tiff',
    // Video
    '.mp4': 'video/mp4',
    '.webm': 'video/webm',
    '.avi': 'video/x-msvideo',
    '.mov': 'video/quicktime',
    '.mkv': 'video/x-matroska',
    '.flv': 'video/x-flv',
    '.wmv': 'video/x-ms-wmv',
    // Audio
    '.mp3': 'audio/mpeg',
    '.wav': 'audio/wav',
    '.ogg': 'audio/ogg',
    '.flac': 'audio/flac',
    '.aac': 'audio/aac',
    '.m4a': 'audio/mp4',
    // Documents
    '.pdf': 'application/pdf',
    '.doc': 'application/msword',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.xls': 'application/vnd.ms-excel',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.ppt': 'application/vnd.ms-powerpoint',
    '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    // Text
    '.txt': 'text/plain',
    '.csv': 'text/csv',
    '.html': 'text/html',
    '.htm': 'text/html',
    '.css': 'text/css',
    '.js': 'application/javascript',
    '.ts': 'application/typescript',
    '.json': 'application/json',
    '.xml': 'application/xml',
    '.md': 'text/markdown',
    '.yaml': 'text/yaml',
    '.yml': 'text/yaml',
    // Archives
    '.zip': 'application/zip',
    '.rar': 'application/vnd.rar',
    '.7z': 'application/x-7z-compressed',
    '.tar': 'application/x-tar',
    '.gz': 'application/gzip',
    '.bz2': 'application/x-bzip2',
    // Fonts
    '.ttf': 'font/ttf',
    '.otf': 'font/otf',
    '.woff': 'font/woff',
    '.woff2': 'font/woff2',
    // Executables / binary
    '.exe': 'application/vnd.microsoft.portable-executable',
    '.dmg': 'application/x-apple-diskimage',
    '.apk': 'application/vnd.android.package-archive',
    '.deb': 'application/vnd.debian.binary-package',
  }
  return map[ext] || 'application/octet-stream'
}

/**
 * Convert a hex string to Uint8Array (used in browser SHA-256).
 */
export function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2)
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16)
  }
  return bytes
}

/**
 * Convert an ArrayBuffer to hex string.
 */
export function bufferToHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Default chunk size for reading files (2MB).
 */
export const DEFAULT_CHUNK_SIZE = 2 * 1024 * 1024

/**
 * Check if a Uint8Array chunk contains null bytes (heuristic for binary).
 */
export function isBinaryChunk(chunk: Uint8Array): boolean {
  for (let i = 0; i < Math.min(chunk.length, 8192); i++) {
    if (chunk[i] === 0) return true
  }
  return false
}
